import { ClientError } from "~/plugins/error";
import { CauseId } from "#shared/errors";

/**
 * Format a 64-bit integer account number as HEX with dashes
 * @param accountNumber 64-bit integer from database users.account_number
 * @returns Formatted account number in HEX with dashes (e.g., "1A2B-3C4D-5E6F-7890")
 */
export const formatAccountNumber = (accountNumber: number | string) => {
    if (!accountNumber) return '';
    
    // Convert to number if needed
    const num = typeof accountNumber === 'number' ? accountNumber : parseInt(accountNumber);
    
    // Convert to hex and pad to 16 characters (64 bits)
    const hex = num.toString(16).toUpperCase().padStart(16, '0');
    
    // Split into groups of 4 characters and join with hyphens
    return hex.match(/.{1,4}/g)?.join('-') || '';
}

/**
 * Parse user-entered account number text and normalize it to a 64-bit integer
 * Handles common character substitutions and formatting
 * Users can enter numbers with or without separators, supporting up to 16 hex digits
 * @param accountNumberText User input text (HEX with or without dashes)
 * @returns 64-bit integer account number
 */
export const parseAccountNumber = (accountNumberText: string): number => {
    if (!accountNumberText) return 0;
    
    // Remove any existing separators and apply character corrections
    const cleanedText = accountNumberText
      .replaceAll(/[ou]/gi, '0')
      .replaceAll(/[il]/gi, '1')
      .replaceAll(/[nz]/gi, '2')
      .replaceAll(/m/gi, '3')
      .replaceAll(/s/gi, '5')
      .replaceAll(/[rtv]/gi, '7')
      .replaceAll(/[qp]/gi, '9')
      .replaceAll(/[^a-f0-9]/gi, '')
      .toLowerCase();

    if (cleanedText.length > 16) { // 16 hex digits = 64 bits
      throw new ClientError(CauseId.INVALID_ACCOUNT_ID, { accountIdText: accountNumberText });
    }

    // Parse the full cleaned text as a 64-bit integer
    // Use BigInt for parsing to avoid precision loss, then convert to number
    return Number(BigInt('0x' + cleanedText));
}

/**
 * Format a South African ID number with proper spacing (XXXXXX XXXX XX X)
 * @param idNumber Numeric ID number as BIGINT (13 digits)
 * @returns Formatted ID number with spaces (e.g., "901230 1234 56 7")
 */
export const formatIdNumber = (idNumber: string | number | bigint): string => {
    if (!idNumber) return '';
    
    // Convert to string and remove any non-numeric characters
    const numericOnly = idNumber.toString().replace(/\D/g, '');
    
    // Return as-is if not 13 digits
    if (numericOnly.length !== 13) return numericOnly;
    
    // Format as XXXXXX XXXX XX X
    return `${numericOnly.slice(0, 6)} ${numericOnly.slice(6, 10)} ${numericOnly.slice(10, 12)} ${numericOnly.slice(12, 13)}`;
}

/**
 * Parse and clean ID number input, removing spaces and non-numeric characters
 * @param idNumberText User input text
 * @returns Clean numeric ID number as BIGINT (number)
 */
export const parseIdNumber = (idNumberText: string): number => {
    if (!idNumberText) return 0;
    
    // Remove all non-numeric characters
    const numericOnly = idNumberText.replace(/\D/g, '');
    
    // Return 0 if empty after cleaning
    if (!numericOnly) return 0;
    
    // Convert to number for BIGINT storage
    const idNumber = parseInt(numericOnly);
    
    // Validate range for BIGINT
    if (isNaN(idNumber) || idNumber <= 0 || idNumber > Number.MAX_SAFE_INTEGER) {
        throw new ClientError(CauseId.INVALID_REQUEST, { idNumberText });
    }
    
    return idNumber;
}

/**
 * Format a phone number from stored 64-bit integer to display format
 * @param phone Phone number as 64-bit integer (e.g., 27811231234)
 * @returns Formatted phone number (e.g., "+27 81 123 1234")
 */
export const formatPhone = (phone: string | number | bigint | undefined): string => {
    if (!phone) return '';
    
    // Convert to string to handle the number
    const phoneStr = phone.toString();
    
    // Handle very long numbers (more than 15 digits)
    if (phoneStr.length > 15) {
        // For very long numbers, format as: +XX XX XXX XXXX XXXXXXXXX
        const countryCode = phoneStr.slice(0, 2);
        const remaining = phoneStr.slice(2);
        
        if (remaining.length >= 9) {
            const group1 = remaining.slice(0, 2);
            const group2 = remaining.slice(2, 5);
            const group3 = remaining.slice(5, 9);
            const group4 = remaining.slice(9);
            return `+${countryCode} ${group1} ${group2} ${group3} ${group4}`;
        }
    }
    
    // Handle standard length numbers (up to 15 digits)
    if (phoneStr.length >= 10) {
        // Assume first 1-3 digits are country code
        let countryCode = '';
        let nationalNumber = '';
        
        // Common country code patterns
        if (phoneStr.startsWith('27') && phoneStr.length === 11) {
            // South African format: 27XXXXXXXXX
            countryCode = '27';
            nationalNumber = phoneStr.slice(2);
        } else if (phoneStr.startsWith('1') && phoneStr.length === 11) {
            // North American format: 1XXXXXXXXXX
            countryCode = '1';
            nationalNumber = phoneStr.slice(1);
        } else if (phoneStr.length === 12 && ['44', '33', '49', '39'].some(cc => phoneStr.startsWith(cc))) {
            // European formats: 44XXXXXXXXXX, 33XXXXXXXXX, etc.
            countryCode = phoneStr.slice(0, 2);
            nationalNumber = phoneStr.slice(2);
        } else {
            // Default: assume first 2 digits are country code
            countryCode = phoneStr.slice(0, 2);
            nationalNumber = phoneStr.slice(2);
        }
        
        // Format the national number
        if (nationalNumber.length === 9) {
            // Standard 9-digit format: XXX XXX XXXX
            return `+${countryCode} ${nationalNumber.slice(0, 2)} ${nationalNumber.slice(2, 5)} ${nationalNumber.slice(5)}`;
        } else if (nationalNumber.length === 10) {
            // 10-digit format: XXX XXX XXXX
            return `+${countryCode} ${nationalNumber.slice(0, 3)} ${nationalNumber.slice(3, 6)} ${nationalNumber.slice(6)}`;
        } else if (nationalNumber.length >= 7) {
            // Generic format for other lengths
            const firstPart = nationalNumber.slice(0, 3);
            const secondPart = nationalNumber.slice(3, 6);
            const thirdPart = nationalNumber.slice(6);
            return `+${countryCode} ${firstPart} ${secondPart} ${thirdPart}`;
        }
    }
    
    // Fallback for shorter numbers
    return `+${phoneStr}`;
}

/**
 * Parse and clean phone number input, converting to 64-bit integer for storage
 * Handles automatic +27 conversion for numbers starting with 0
 * @param phoneText User input text (can include +, spaces, dashes, parentheses)
 * @returns 64-bit integer phone number for database storage
 */
export const parsePhone = (phoneText: string): number => {
    if (!phoneText || phoneText.trim() === '') return 0;
    
    // Remove all non-numeric characters except +
    let cleaned = phoneText.replace(/[^\d+]/g, '');
    
    // Handle leading 0 - convert to +27 (South African format)
    if (cleaned.startsWith('0')) {
        cleaned = '27' + cleaned.slice(1);
    }
    
    // Remove + symbol for storage
    cleaned = cleaned.replace(/\+/g, '');
    
    // If cleaned is empty after processing, return 0
    if (!cleaned) return 0;
    
    // Convert to number
    const phoneNumber = parseInt(cleaned);
    
    // Validate reasonable phone number range (must be positive and not too large)
    if (isNaN(phoneNumber) || phoneNumber <= 0 || phoneNumber > Number.MAX_SAFE_INTEGER) {
        throw new ClientError(CauseId.INVALID_REQUEST, { phoneText });
    }
    
    return phoneNumber;
}

/**
 * Validate South African ID number format and checksum
 * @param idNumber ID number string or number (with or without spaces)
 * @returns true if valid, false otherwise
 */
export const validateIdNumber = (idNumber: string | number): boolean => {
    if (!idNumber) return true; // Allow empty
    
    const idNumberStr = idNumber.toString();
    const numericOnly = idNumberStr.replace(/\D/g, '');
    
    // Must be exactly 13 digits
    if (numericOnly.length !== 13) return false;
    
    // Check if original input had non-numeric characters mixed with valid length
    // If the original has non-numeric chars but results in exactly 13 digits, it's suspicious
    const originalNumericOnly = idNumberStr.replace(/\D/g, '');
    if (originalNumericOnly.length !== idNumberStr.replace(/[\s\-.]/g, '').length && numericOnly.length === 13) {
        return false; // Contains non-numeric chars that aren't standard separators
    }
    
    // Basic format validation (YYMMDD + sequence + citizenship + gender + checksum)
    const digits = numericOnly.split('').map(Number);
    
    // Validate date part (first 6 digits - YYMMDD)
    const month = parseInt(numericOnly.slice(2, 4));
    const day = parseInt(numericOnly.slice(4, 6));
    
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;
    if (digits.length !== 13) return false;
    
    // Calculate checksum using Luhn algorithm
    let sum = 0;
    for (let i = 0; i < 12; i++) {
        let digit = digits[i];
        if (digit === undefined) return false;
        if (i % 2 === 1) {
            digit *= 2;
            if (digit > 9) digit = Math.floor(digit / 10) + (digit % 10);
        }
        sum += digit;
    }
    
    const checksum = (10 - (sum % 10)) % 10;
    return checksum === digits[12];
}

/**
 * Format time in seconds to a human-readable string
 * @param timeSec Time in seconds, null/undefined means unlimited time
 * @returns Formatted time string or "No time limit" for unlimited
 */
export const formatTime = (timeSec: number | null | undefined): string => {
    if (!timeSec) {
        return 'No time limit';
    }
    
    const minutes = Math.round(timeSec / 60);
    
    if (minutes < 60) {
        return `${minutes} minute${minutes === 1 ? '' : 's'}`;
    }
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (remainingMinutes === 0) {
        return `${hours} hour${hours === 1 ? '' : 's'}`;
    }
    
    return `${hours}h ${remainingMinutes}m`;
};
